import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { IncidentService } from 'src/app/services/api/incident.service';
import { MaterialModule } from 'src/app/material/material.module';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-dialog-pre-operative-incident-form',
  standalone: true,
  templateUrl: './dialog-pre-operative-incident-form.component.html',
  styleUrls: ['./dialog-pre-operative-incident-form.component.css'],
  imports: [CommonModule, ReactiveFormsModule, MaterialModule]
})
export class DialogPreOperativeIncidentForm implements OnInit {
  obsForm!: FormGroup;
  destinosBase: string[] = ['Lima', 'Cusco', 'Huaraz', 'Otros'];
  incidentReasons: string[] = [
    'Cotización',
    'Datos de vuelos',
    'Datos de pasajeros',
    'Itinerario',
    'Otro'
  ];

  citiesPeru: string[] = [
    'Amazonas',
    'Apurímac',
    'Arequipa',
    'Ayacucho',
    'Cajamarca',
    'Callao',
    'Huancavelica',
    'Huánuco',
    'Ica',
    'Junín',
    'La Libertad',
    'Lambayeque',
    'Loreto',
    'Madre de Dios',
    'Moquegua',
    'Pasco',
    'Piura',
    'Puno',
    'San Martín',
    'Tacna',
    'Tumbes',
    'Ucayali'
  ];
  filteredCities!: Observable<string[]>;

  usuario: string = '';
  isSubmitting: boolean = false;

  constructor(
    private readonly incidentService: IncidentService,
    public dialogRef: MatDialogRef<DialogPreOperativeIncidentForm>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.usuario = this.data.usuario;
    if (!this.usuario) {
      throw new Error(
        'El usuario es un valor requerido y no puede estar vacío.'
      );
    }
  }

  ngOnInit(): void {
    this.obsForm = this.fb.group({
      incidentDate: [
        { value: new Date(), disabled: true },
        Validators.required
      ],
      destination: ['', Validators.required],
      otherDestination: [''],
      incidentReason: ['', Validators.required],
      otherReason: [''],
      description: ['', Validators.required],
      deadline: ['', Validators.required]
    });

    this.obsForm.get('destination')?.valueChanges.subscribe((val) => {
      if (val === 'Otros') {
        this.obsForm
          .get('otherDestination')
          ?.setValidators([Validators.required, this.cityValidator.bind(this)]);
      } else {
        this.obsForm.get('otherDestination')?.clearValidators();
        this.obsForm.get('otherDestination')?.setValue('');
      }
      this.obsForm.get('otherDestination')?.updateValueAndValidity();
    });

    this.obsForm.get('incidentReason')?.valueChanges.subscribe((val) => {
      if (val === 'Otro') {
        this.obsForm.get('otherReason')?.setValidators(Validators.required);
      } else {
        this.obsForm.get('otherReason')?.clearValidators();
        this.obsForm.get('otherReason')?.setValue('');
      }
      this.obsForm.get('otherReason')?.updateValueAndValidity();
    });

    this.filteredCities = this.obsForm
      .get('otherDestination')!
      .valueChanges.pipe(
        startWith(''),
        map((value) => this._filterCities(value || ''))
      );
  }

  private _filterCities(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.citiesPeru.filter((city) =>
      city.toLowerCase().includes(filterValue)
    );
  }

  private cityValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return this.citiesPeru.includes(control.value)
      ? null
      : { invalidCity: true };
  }

  async handleConfirm() {
    if (this.obsForm.invalid) {
      this.obsForm.markAllAsTouched();
      return;
    }

    const val = this.obsForm.getRawValue();
    const finalDestino =
      val.destination === 'Otros' ? val.otherDestination : val.destination;
    const finalMotivo =
      val.incidentReason === 'Otro' ? val.otherReason : val.incidentReason;

    const formatToLimaIso = (date: Date) =>
      moment(date).utcOffset('-05:00').format();

    const newPreOperativeIncident = {
      itineraryId: this.data?.itinerario?.id,
      destination: finalDestino,
      incidentReason: finalMotivo,
      deadline: formatToLimaIso(val.deadline),
      description: val.description,
      incidentDate: formatToLimaIso(val.incidentDate)
    };

    this.isSubmitting = true;

    this.incidentService
      .createPreOpIncident({
        itineraryId: newPreOperativeIncident.itineraryId,
        destination: newPreOperativeIncident.destination,
        incidentReason: newPreOperativeIncident.incidentReason,
        description: newPreOperativeIncident.description,
        deadline: newPreOperativeIncident.deadline,
        incidentDate: newPreOperativeIncident.incidentDate,
        createdBy: this.usuario
      })
      .subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          console.log({ response });
          if (typeof response?.createIncidenciaPreOperativa === 'string') {
            Swal.fire({
              position: 'center',
              icon: 'success',
              iconHtml: '<i class="ti ti-circle-check"></i>',
              titleText: '¡Observación creada!',
              text: `Se creo correctamente la observación de "${finalMotivo}" en Programación de Files y Listado de Itinerarios.`,
              showConfirmButton: true,
              confirmButtonText: 'Ir al listado de observaciones',
              width: '600px',
              didClose: () => {
                this.dialogRef.close({
                  ...newPreOperativeIncident,
                  id: response.createIncidenciaPreOperativa
                });
              }
            });
          } else {
            Swal.fire({
              position: 'center',
              icon: 'error',
              iconHtml: '<i class="ti ti-circle-x"></i>',
              titleText: 'Error al crear observación',
              text: 'No se pudo crear la observación de Datos de vuelos. Inténtalo nuevamente.',
              showConfirmButton: true,
              confirmButtonText: 'Reintentar',
              width: '600px'
            });
          }
        },
        error: (error: any) => {
          this.isSubmitting = false;
          console.error('Error al actualizar el itinerario:', error);
          Swal.fire({
            position: 'center',
            icon: 'error',
            iconHtml: '<i class="ti ti-circle-x"></i>',
            titleText: 'Error al crear observación',
            text: 'No se pudo crear la observación de Datos de vuelos. Inténtalo nuevamente.',
            showConfirmButton: true,
            confirmButtonText: 'Reintentar',
            width: '600px'
          });
        }
      });
  }
}
